import type { StackFrame, Stacktrace } from './types';

/**
 * Parse a JavaScript `Error.stack` into structured frames.
 *
 * Stack formats are not standardised, so this handles the two shapes that cover
 * essentially all traffic: V8/Chrome/Node (`at fn (file:line:col)`) and
 * SpiderMonkey/JavaScriptCore (`fn@file:line:col`).
 */

const V8_FRAME = /^\s*at (?:(.+?)\s+\()?(?:(.+?):(\d+):(\d+)|([^)]+))\)?\s*$/;
const GECKO_FRAME = /^\s*(?:(.*?)@)?(.*?):(\d+):(\d+)\s*$/;

/** Path fragments that mark a frame as library rather than application code. */
const VENDOR_MARKERS = ['/node_modules/', 'node:internal', '/.pnpm/', 'webpack/runtime'];

export function parseStacktrace(stack: string | undefined): Stacktrace | undefined {
  if (!stack) return undefined;

  const frames: StackFrame[] = [];

  for (const line of stack.split('\n')) {
    const frame = parseLine(line);
    if (frame) frames.push(frame);
  }

  if (frames.length === 0) return undefined;

  // Stacks arrive innermost-first; the server expects oldest-caller-first so the
  // crashing frame is last.
  frames.reverse();
  return { frames };
}

function parseLine(line: string): StackFrame | undefined {
  const v8 = V8_FRAME.exec(line);
  if (v8) {
    // A frame with no line/column (`at <anonymous>`) carries no useful location.
    if (!v8[2]) return undefined;
    // An anonymous async frame has no parentheses to separate the marker from the
    // location: `at async /app/app.cjs:9:11908`. Left in, the marker becomes part
    // of the path and no source map can ever match it.
    const file = v8[1] === undefined ? v8[2].replace(/^async\s+/, '') : v8[2];
    return buildFrame(v8[1], file, v8[3], v8[4]);
  }

  const gecko = GECKO_FRAME.exec(line);
  if (gecko && gecko[2]) {
    return buildFrame(gecko[1], gecko[2], gecko[3], gecko[4]);
  }

  return undefined;
}

function buildFrame(
  fn: string | undefined,
  file: string,
  line: string | undefined,
  column: string | undefined,
): StackFrame {
  const absPath = file.trim();
  return {
    function: fn?.trim() || '<anonymous>',
    filename: absPath,
    abs_path: absPath,
    lineno: line ? Number(line) : undefined,
    colno: column ? Number(column) : undefined,
    in_app: isInApp(absPath),
  };
}

/**
 * Whether a path looks like application code.
 *
 * This drives grouping on the server, which keys on in-app frames — getting it
 * wrong merges unrelated crashes that happen to share a library frame.
 */
export function isInApp(path: string): boolean {
  if (!path) return false;
  if (path.startsWith('native') || path === '<anonymous>') return false;
  return !VENDOR_MARKERS.some((marker) => path.includes(marker));
}

/** Extract the exception type from an Error, falling back sensibly. */
export function errorType(error: unknown): string {
  if (error instanceof Error) {
    return error.name || error.constructor?.name || 'Error';
  }
  return typeof error === 'string' ? 'Error' : 'UnknownError';
}

/** Extract a human-readable message from any thrown value. */
export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    try {
      return JSON.stringify(error);
    } catch {
      // A circular or exotic object still deserves a report.
      return Object.prototype.toString.call(error);
    }
  }
  return String(error);
}
